import {Chart} from './chart';
import {Constants as constants} from '../config/constants';
import {Point} from '@/types/primitives';
import {ChartData, CategoryViewMappingCollection, HelperLinesMapping} from '@/types/data';
import {Helpers} from '../common/helpers';
import {Selection as D3Selection} from 'd3';

export class CategoryParts {

    public get data() {
        return {
            content: this.categoryContent
        };
    }
    private helperLines = [] as Array<{ line: D3Selection<any, any, any, any>, angle: number, category: string | null }>;
    private categoryContent = {} as CategoryViewMappingCollection;

    constructor(private chart: Chart, private chartData: ChartData) {
    }

    public render() {
        this.drawHelperLines();
        this.drawCategories();
        this.drawLabels();
    }

    private generateLargeCirclePoints() {
        const resultPoints = [] as HelperLinesMapping[];
        const largeLimitations = {
            start: 0,
            end: constants.arcs.large.angle
        };
        const step = (largeLimitations.end - largeLimitations.start) / (constants.categoryKeysToArcs.large.length + 1);

        for (let i = 1; i < constants.categoryKeysToArcs.large.length + 1; i++) {
            resultPoints.push({
                angle: largeLimitations.start + step * i,
                points: [
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.large.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.large.r + constants.arcDiffStep.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.large.r + constants.arcDiffStep.r + constants.labelLimitDistance)
                ]
            });
        }

        return resultPoints;
    }

    private generateMiddleCirclePoints() {
        const resultPoints = [] as HelperLinesMapping[];
        const largeLimitations = {
            start: constants.arcs.large.angle + constants.arcs.small.angle,
            end: 360
        };
        const step = (largeLimitations.end - largeLimitations.start) / (constants.categoryKeysToArcs.middle.length + 1);

        for (let i = 1; i < constants.categoryKeysToArcs.middle.length + 1; i++) {
            resultPoints.push({
                angle: largeLimitations.start + step * i,
                points: [
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.middle.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.middle.r + constants.arcDiffStep.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.middle.r + constants.arcDiffStep.r + constants.labelLimitDistance)
                ]
            });
        }

        return resultPoints;
    }

    private generateSmallCirclePoints() {
        const resultPoints = [] as HelperLinesMapping[];
        const largeLimitations = {
            start: constants.arcs.large.angle,
            end: constants.arcs.large.angle + constants.arcs.small.angle
        };
        const step = (largeLimitations.end - largeLimitations.start) / (constants.categoryKeysToArcs.small.length + 1);

        for (let i = 1; i < constants.categoryKeysToArcs.small.length + 1; i++) {
            resultPoints.push({
                angle: largeLimitations.start + step * i,
                points: [
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.small.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.small.r + constants.arcDiffStep.r),
                    Helpers.calculatePointOnArc(this.chart.toolList.pathRenderer.data.center, largeLimitations.start + step * i, constants.arcs.small.r + constants.arcDiffStep.r + constants.labelLimitDistance)
                ]
            });
        }

        return resultPoints;
    }

    private drawHelperLines() {
        let linePoints = [] as HelperLinesMapping[];

        linePoints = linePoints.concat(this.generateLargeCirclePoints());
        linePoints = linePoints.concat(this.generateSmallCirclePoints());
        linePoints = linePoints.concat(this.generateMiddleCirclePoints());

        linePoints.map((entry) => {
            const lineEntry = this.chart.node.chart
                .append('path')
                .attr('d',
                    ['M', `${this.chart.toolList.pathRenderer.data.center.x} ${this.chart.toolList.pathRenderer.data.center.y}`]
                        .concat(entry.points.map((point) => {
                            return `L ${point.x} ${point.y} M ${point.x} ${point.y}`;
                        }))
                        .join(' ')
                );

            this.helperLines.push({
                line: lineEntry,
                angle: entry.angle,
                category: null
            });
        });
    }

    private calculateCategories() {
        const firstTypeKey = Object.keys(this.chartData).shift();
        const allowedCategories = [].concat(constants.categoryKeysToArcs.large.slice(0)).concat(constants.categoryKeysToArcs.small.slice(0)).concat(constants.categoryKeysToArcs.middle.slice(0));
        const trianglePoints = {} as { [index: string]: { [index: string]: Point[] } };

        allowedCategories.map((category, index) => {
            const targetPath = this.helperLines[index].line.node() as SVGPathElement;
            let categoryTotalPercentage = 0;
            let lastPercentage = 0;
            const length = {
                normal: targetPath.getTotalLength() - constants.arcDiffStep.r - constants.labelLimitDistance,
                upper: targetPath.getTotalLength() - constants.labelLimitDistance,
                label: targetPath.getTotalLength()
            };

            if (typeof trianglePoints[category] === 'undefined') {
                trianglePoints[category] = {};
            }
            this.helperLines[index].category = category;

            for (const type in this.chartData) {
                if (categoryTotalPercentage < 1) {
                    categoryTotalPercentage += this.chartData[type][category] ? this.chartData[type][category].percentage : 0;

                    if (categoryTotalPercentage > 1) {
                        trianglePoints[category][type] = [
                            {
                                x: targetPath.getPointAtLength(lastPercentage * length.normal).x,
                                y: targetPath.getPointAtLength(lastPercentage * length.normal).y
                            },
                            {
                                x: targetPath.getPointAtLength(length.upper).x,
                                y: targetPath.getPointAtLength(length.upper).y
                            }
                        ];
                    } else {
                        trianglePoints[category][type] = [
                            {
                                x: targetPath.getPointAtLength(lastPercentage * length.normal).x,
                                y: targetPath.getPointAtLength(lastPercentage * length.normal).y
                            },
                            {
                                x: targetPath.getPointAtLength(categoryTotalPercentage * length.normal).x,
                                y: targetPath.getPointAtLength(categoryTotalPercentage * length.normal).y
                            }
                        ];
                    }

                    lastPercentage = categoryTotalPercentage;
                }
            }
        });

        return trianglePoints;
    }

    private drawCategories() {
        const trianglePoints = this.calculateCategories();

        for (const category in trianglePoints) {
            if (trianglePoints.hasOwnProperty(category)) {
              let categoryCounter = 1;

              if (typeof this.categoryContent[category] === 'undefined') {
                this.categoryContent[category] = {
                  triangles: {},
                  label: null,
                  group: this.chart.node.chart
                    .append('g')
                    .attr('category', category)
                };
              }

              for (const type in trianglePoints[category]) {
                if (trianglePoints[category].hasOwnProperty(type)) {
                  const triangle = Helpers.calculateTriangle(
                    trianglePoints[category][type][0],
                    trianglePoints[category][type][1],
                    categoryCounter === 1 ? Helpers.normalizeTriangleBasement(
                      Helpers.calculateVectorLength(trianglePoints[category][type][0], trianglePoints[category][type][1]),
                      constants.categoryRectangleBase,
                      constants.minimalTriangleBasedMultiplier
                    ) : constants.categoryRectangleBase
                  );

                  categoryCounter++;

                  this.categoryContent[category].triangles[type] = this.categoryContent[category].group
                    .append('path')
                    .attr('d', [
                      'M', `${triangle[1].x} ${triangle[1].y}`,
                      `${triangle[2].x} ${triangle[2].y}`,
                      `${triangle[0].x} ${triangle[0].y}`,
                      'Z'
                    ].join(' '))
                    .attr('class', `shape-${type.split('-').shift()}`);
                }
              }
            }
        }
    }

    private drawLabels() {
        const firstTypeKey = Object.keys(this.chartData).shift();
        const allowedCategories = Object.keys(this.chartData[firstTypeKey!]);

        allowedCategories.map((category, index) => {
            const labelEntry = this.categoryContent[category].group
                .append('text')
                .text(constants.publicNamesOfCategories[category])
                .attr('class', 'label');

            this.categoryContent[category].label = this.normalizeLabel(labelEntry, category);
        });
    }

    private normalizeLabel(label: D3Selection<any, any, any, any>, category: string) {
        const targetLine = this.helperLines.filter((line) => line.category === category)[0].line.node() as SVGPathElement;
        const targetAngle = this.helperLines.filter((line) => line.category === category)[0].angle;
        const labelPosition = targetLine.getPointAtLength(targetLine.getTotalLength());

        const labelSize = (label.node() as SVGTextElement).getBBox();
        const normalizedAngle = (targetAngle >= 180 ? 90 : -90) + targetAngle;
        const normalizeValue = Helpers.rotateVector(normalizedAngle, {
            x: targetAngle >= 180 ? -labelSize.width : 0,
            y: labelSize.height / 4

        });

        return label
            .attr('transform-origin', 'left top')
            .attr('transform', `translate(${labelPosition.x + normalizeValue.x}, ${labelPosition.y + normalizeValue.y}) rotate(${normalizedAngle})`);
    }
}
